import React, { useState, useEffect } from "react";
import { Lock, Mail, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { authAPI } from "../../services/api";

const SSO_ERROR_MESSAGES = {
  access_denied:
    "You don't have access to Wasabi Shinobi in Okta. Ask IT to grant your account access.",
  state: "Your sign-in session expired. Please try signing in again.",
  token: "Single sign-on failed. Please try again, or sign in with email and password.",
  unavailable: "The identity provider is unreachable right now. Try again in a moment.",
  inactive: "Your account is deactivated. Contact an administrator.",
};

const LoginForm = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ssoEnabled, setSsoEnabled] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  useEffect(() => {
    authAPI
      .ssoConfig()
      .then((r) => setSsoEnabled(!!(r && r.data && r.data.enabled)))
      .catch(() => setSsoEnabled(false));
    try {
      const code = new URLSearchParams(window.location.search).get("sso_error");
      if (code) {
        setError(SSO_ERROR_MESSAGES[code] || "Single sign-on failed. Please try again.");
      }
    } catch (e) {
      /* no-op */
    }
  }, []);

  const startSsoLogin = () => {
    const apiBase = process.env.REACT_APP_API_URL || "";
    window.location.href = `${apiBase}/api/v1/auth/sso/login`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);

      if (!result.success) {
        let errorMessage = result.error || "Login failed. Please check your credentials.";
        if (errorMessage.toLowerCase().includes("locked")) {
          errorMessage =
            "Account temporarily locked due to multiple failed attempts. Please try again in a few minutes.";
        } else if (
          errorMessage.toLowerCase().includes("invalid") ||
          errorMessage.toLowerCase().includes("incorrect")
        ) {
          errorMessage = "Invalid email or password. Please check your credentials and try again.";
        }
        setError(errorMessage);
        setLoading(false);
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      setError("An unexpected error occurred: " + (err.message || "Unknown error"));
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const Spinner = () => (
    <span className="flex items-center justify-center">
      <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      Processing...
    </span>
  );

  return (
    <div className="min-h-screen bg-cyber-50 cyber-grid-bg flex items-center justify-center p-4 relative overflow-hidden">
      {/* Subtle brand-green ambient wash */}
      <div className="absolute top-20 left-20 w-96 h-96 bg-wasabi-300 opacity-20 rounded-full blur-3xl animate-pulse-slow"></div>
      <div
        className="absolute bottom-20 right-20 w-96 h-96 bg-wasabi-200 opacity-20 rounded-full blur-3xl animate-pulse-slow"
        style={{ animationDelay: "1s" }}
      ></div>

      <div className="w-full max-w-md relative z-10 flex flex-col items-center justify-center">
        <div className="text-center mb-6 w-full">
          <div className="inline-flex flex-col items-center justify-center mb-3">
            <img
              src="/logos/wasabi-shinobi-mark-256.png"
              alt="Wasabi Shinobi"
              className="w-28 h-28 rounded-2xl animate-pulse-slow"
            />
            <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-900">Wasabi Shinobi</h1>
          </div>
          <p className="text-slate-500 text-sm">Internal Wasabi Red Team Automation</p>
        </div>

        <div className="cyber-card rounded-lg p-8 shadow-2xl w-full">
          <div className="mb-6 pb-4 border-b border-slate-200">
            <h2 className="text-2xl font-bold text-wasabi-600 text-center">LOGIN</h2>
          </div>

          <form action="#" onSubmit={handleLogin} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-shinobi-purple" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="terminal-input w-full pl-11 pr-4 py-3 rounded focus:ring-2 focus:ring-wasabi-500/30"
                  placeholder="Email"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-shinobi-purple" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="terminal-input w-full pl-11 pr-11 py-3 rounded focus:ring-2 focus:ring-shinobi-purple"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-shinobi-purple transition"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-cyber w-full py-3 rounded font-semibold text-sm uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Spinner /> : "Access System"}
            </button>
          </form>

          {ssoEnabled && (
            <>
              <div className="my-4 flex items-center gap-3 text-gray-600 text-[10px] uppercase tracking-widest">
                <span className="flex-1 h-px bg-cyber-300" />
                or
                <span className="flex-1 h-px bg-cyber-300" />
              </div>
              <button
                type="button"
                onClick={startSsoLogin}
                className="w-full py-3 rounded font-semibold text-sm uppercase tracking-wider border border-cyber-300 text-slate-800 hover:bg-cyber-100 transition-colors"
              >
                Sign in with Okta
              </button>
            </>
          )}

          <div className="mt-3 text-center">
            <a
              href="/forgot-password"
              className="text-xs text-gray-500 hover:text-shinobi-pink transition-colors"
            >
              Forgot your password?
            </a>
          </div>

          <div className="mt-4 text-center text-xs text-gray-500">
            <p>Authorized personnel only</p>
            <p className="mt-1">All activities are monitored and logged</p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-500">
          <span>Powered by</span>
          <img src="/wasabi-logo.svg" alt="Wasabi" className="h-4 w-auto" />
          <span>Internal Red Team</span>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
